import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * The product list inside a Shop the look drawer.
 *
 * Keeps each row's hidden variant input in sync with its variant picker, and adds every
 * product in the look to the cart in one request.
 *
 * @extends Component
 */
class ShopTheLookProducts extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(CartErrorEvent.eventName, this.#enableButtons);
    this.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(CartErrorEvent.eventName, this.#enableButtons);
    this.removeEventListener('submit', this.#handleSubmit);
  }

  /**
   * Locks the row's button while its request is in flight, so repeated clicks before the
   * drawer closes cannot add the same product several times.
   *
   * @param {Event} event
   */
  #handleSubmit = (event) => {
    const button = /** @type {Element} */ (event.target)
      ?.closest('.stl-product')
      ?.querySelector('.stl-product__add-btn');

    if (button instanceof HTMLButtonElement) button.disabled = true;

    // Hand over to the cart on the click, not when the request comes back — waiting for the
    // round trip is what made this feel slow. The cart drawer fills itself in once the
    // cart:update event lands.
    this.#openCart();
  };

  /**
   * Closes this drawer, then brings the cart drawer forward.
   *
   * The wait matters: a dialog records the page's scroll position when it opens, and while
   * another dialog still holds the body fixed that reading comes back as 0 — so closing the
   * cart afterwards would jump the shopper back to the top of the page. Only the close
   * animation is awaited, never the cart request.
   */
  #openCart = () => {
    if (this.openingCart) return;
    this.openingCart = true;

    const host = this.closest('dialog-component');
    const dialog = host?.querySelector('dialog');

    const showCart = () => {
      if (!this.openingCart) return;
      this.openingCart = false;

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && !cartDrawer.hasAttribute('auto-open')) cartDrawer.open?.();
    };

    // Wait for this dialog to actually finish closing. It restores the page scroll and releases
    // the body lock on the way out; opening the cart any earlier makes it record a scroll
    // position of 0, and closing the cart would then throw the shopper back to the top.
    // The timeout is only a safety net in case the close event never arrives.
    dialog?.addEventListener('close', showCart, { once: true });
    setTimeout(showCart, 600);

    void host?.closeDialog?.();
  };

  /**
   * Hands over to the cart drawer once something lands in the cart: this drawer closes
   * first, because two modal dialogs cannot be open at the same time.
   *
   * @param {Event} event
   */
  #handleCartAdd = async (event) => {
    if (/** @type {any} */ (event).detail?.data?.didError) {
      this.#enableButtons();
      return;
    }

    // Single adds already handed over on submit; this covers "add all", which has no form.
    this.#enableButtons();
    this.#openCart();
  };

  /**
   * Re-arms the buttons that are still for sale, so the look can be opened again later.
   */
  #enableButtons = () => {
    for (const row of this.querySelectorAll('.stl-product')) {
      const variantInput = row.querySelector('input[name="id"]');
      const button = row.querySelector('.stl-product__add-btn');
      const sellable = variantInput instanceof HTMLInputElement && !variantInput.disabled;

      if (button instanceof HTMLButtonElement) button.disabled = !sellable;
    }
  };

  /**
   * Points a row's form at the variant the shopper picked and refreshes its price.
   *
   * @param {Event} event - The change event coming from a variant picker.
   */
  selectVariant(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const row = select.closest('.stl-product');
    if (!row) return;

    const variantInput = row.querySelector('input[name="id"]');
    const button = row.querySelector('.stl-product__add-btn');
    const price = row.querySelector('.stl-product__price');
    const option = select.selectedOptions[0];
    if (!option) return;

    const available = option.dataset.available === 'true';

    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = option.value;
      variantInput.disabled = !available;
    }

    if (button instanceof HTMLButtonElement) {
      button.disabled = !available;
    }

    if (price && option.dataset.price) {
      price.textContent = option.dataset.price;
    }
  }

  /**
   * Adds every available product in the look to the cart in a single request.
   */
  async addAll() {
    const { addAllButton } = this.refs;
    const items = [];

    for (const row of this.querySelectorAll('.stl-product')) {
      const variantInput = row.querySelector('input[name="id"]');
      if (!(variantInput instanceof HTMLInputElement) || variantInput.disabled || !variantInput.value) continue;
      items.push({ id: Number(variantInput.value), quantity: 1 });
    }

    if (items.length === 0) return;

    if (addAllButton instanceof HTMLButtonElement) addAllButton.disabled = true;
    this.#openCart();

    try {
      const addUrl = Theme.routes.cart_add_url.endsWith('.js')
        ? Theme.routes.cart_add_url
        : `${Theme.routes.cart_add_url}.js`;

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const result = await response.json();

      if (!response.ok) {
        this.dispatchEvent(new CartErrorEvent(this.id, result.description || result.message || ''));
        return;
      }

      // #handleCartAdd picks this up and hands over to the cart drawer.
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'shop-the-look-products',
          itemCount: items.length,
        })
      );
    } catch (error) {
      this.dispatchEvent(new CartErrorEvent(this.id, error instanceof Error ? error.message : String(error)));
    } finally {
      if (addAllButton instanceof HTMLButtonElement) addAllButton.disabled = false;
    }
  }
}

/**
 * The horizontal track of looks.
 *
 * The track is a plain scroll container with CSS scroll snapping, so the arrows only need to
 * push it by a page worth of cards and let snapping settle the final position.
 *
 * @extends Component
 */
class ShopTheLookSlider extends Component {
  requiredRefs = ['slider'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    const { slider } = this.refs;
    if (!(slider instanceof HTMLElement)) return;

    slider.addEventListener('scroll', this.#updateArrows, { passive: true });

    this.#resizeObserver = new ResizeObserver(this.#updateArrows);
    this.#resizeObserver.observe(slider);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { slider } = this.refs;
    if (slider instanceof HTMLElement) slider.removeEventListener('scroll', this.#updateArrows);

    this.#resizeObserver?.disconnect();
  }

  /**
   * Scrolls back by a page of looks.
   */
  previous() {
    this.#scrollByPage(-1);
  }

  /**
   * Scrolls forward by a page of looks.
   */
  next() {
    this.#scrollByPage(1);
  }

  /**
   * @param {-1 | 1} direction
   */
  #scrollByPage(direction) {
    const { slider } = this.refs;
    if (!(slider instanceof HTMLElement)) return;

    const item = slider.firstElementChild;
    const gap = parseFloat(getComputedStyle(slider).columnGap) || 0;
    const itemWidth = item instanceof HTMLElement ? item.getBoundingClientRect().width + gap : 0;

    // Whole cards only: with 4.4 items per view a full-viewport jump would leave the track
    // parked mid-card, and snapping would then pull it somewhere the shopper did not ask for.
    const perPage = itemWidth > 0 ? Math.max(1, Math.floor(slider.clientWidth / itemWidth)) : 1;
    const step = itemWidth > 0 ? itemWidth * perPage : slider.clientWidth;

    // scrollLeft runs negative in right-to-left documents, so the sign of the step follows it.
    const sign = getComputedStyle(slider).direction === 'rtl' ? -1 : 1;

    slider.scrollBy({ left: step * direction * sign, behavior: 'smooth' });
  }

  /**
   * Keeps each arrow in step with what the track can still reach, and hides both when every
   * look already fits on screen.
   */
  #updateArrows = () => {
    const { slider, previous, next } = this.refs;
    if (!(slider instanceof HTMLElement)) return;

    const maxScroll = slider.scrollWidth - slider.clientWidth;
    const position = Math.abs(slider.scrollLeft);
    const canScroll = maxScroll > 1;

    if (previous instanceof HTMLButtonElement) previous.disabled = !canScroll || position <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = !canScroll || position >= maxScroll - 1;
  };
}

if (!customElements.get('shop-the-look-products')) {
  customElements.define('shop-the-look-products', ShopTheLookProducts);
}

if (!customElements.get('shop-the-look-slider')) {
  customElements.define('shop-the-look-slider', ShopTheLookSlider);
}
